import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { formatINR } from "../../components/Price";
import { BUSINESS } from "../../config/business";
import {
  FIRST_SERVICE,
  SERVICE_PLANS,
  MOTOR_SERVICING,
  COMPONENT_LABOUR,
  SPARE_PART_LABOUR,
  SERVICE_PROMISES,
} from "../../config/services";
import "./service.css";

function LabourTable({ title, rows }) {
  return (
    <div className="labour-card">
      <h3>{title}</h3>
      <table className="labour-table">
        <thead>
          <tr>
            <th scope="col">Job</th>
            <th scope="col">Labour cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.item}>
              <td>{r.item}</td>
              <td className="labour-amount">{formatINR(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Service() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Service & Repair | ${BUSINESS.brand}`;
  }, []);

  const bookMsg = encodeURIComponent(
    "Hi, I'd like to book a service for my cycle. Could you tell me the next available slot?"
  );

  return (
    <div className="service-shell">
      <Header />

      <main>
        {/* Hero ---------------------------------------------------- */}
        <section className="service-hero">
          <div className="service-hero-inner">
            <p className="service-eyebrow">Service &amp; Repair</p>
            <h1>Service your electric cycle</h1>
            <p className="service-lead">
              Full workshop servicing for electric and conventional bicycles —
              any brand, whether or not you bought it from us. Transparent,
              published rates with no surprises on the invoice.
            </p>
            <div className="service-hero-actions">
              <a
                className="btn btn-light"
                href={`https://wa.me/${BUSINESS.whatsapp}?text=${bookMsg}`}
                target="_blank"
                rel="noreferrer"
              >
                Book on WhatsApp
              </a>
              <a className="btn btn-outline-light" href={`tel:+${BUSINESS.phoneRaw}`}>
                Call {BUSINESS.phone}
              </a>
            </div>
            <p className="service-hero-note">
              All prices in {BUSINESS.currencyLabel}, inclusive of GST. Spare
              parts charged separately at actuals.
            </p>
          </div>
        </section>

        <div className="service-body">
          {/* First service ---------------------------------------- */}
          <section className="first-service">
            <div className="first-service-badge">{FIRST_SERVICE.name}</div>
            <div className="first-service-text">
              <h2>{FIRST_SERVICE.includes}</h2>
              <p>
                Your first service after purchase is on us — bring the cycle in
                and we will set it up properly once it has bedded in.
              </p>
            </div>
            <div className="first-service-price">{FIRST_SERVICE.price}</div>
          </section>

          {/* Plans ------------------------------------------------- */}
          <section className="service-plans" aria-label="Service packages">
            {SERVICE_PLANS.map((plan) => (
              <article className="plan-card" key={plan.id}>
                <header className="plan-head">
                  <h2>{plan.name}</h2>
                  <p>{plan.tagline}</p>
                </header>

                <div className="plan-prices">
                  {plan.prices.map((p) => (
                    <div className="plan-price" key={p.label}>
                      <span className="plan-price-label">{p.label}</span>
                      <span className="plan-price-amount">
                        {formatINR(p.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="plan-price plan-price-alt">
                    <span className="plan-price-label">
                      {plan.generalService.label}
                    </span>
                    <span className="plan-price-amount">
                      {formatINR(plan.generalService.amount)}
                    </span>
                  </div>
                </div>

                <div className="plan-lists">
                  <div>
                    <h4>{plan.mechanicalHeading}</h4>
                    <ul>
                      {plan.mechanical.map((i) => (
                        <li key={i}>{i}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4>{plan.electricalHeading}</h4>
                    <ul>
                      {plan.electrical.map((i) => (
                        <li key={i}>{i}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </section>

          {/* Motor servicing --------------------------------------- */}
          <section className="motor-service">
            <div>
              <h2>{MOTOR_SERVICING.name}</h2>
              <p>{MOTOR_SERVICING.description}</p>
            </div>
            <div className="motor-price">{formatINR(MOTOR_SERVICING.amount)}</div>
          </section>

          {/* Labour rates ------------------------------------------ */}
          <section className="labour-section">
            <h2 className="labour-heading">Labour rates</h2>
            <p className="labour-note">
              The rates below are for labour only. Any spare part required is
              charged separately at actuals, and we tell you the part cost before
              we order or fit it.
            </p>
            <div className="labour-grid">
              <LabourTable
                title="Component services"
                rows={COMPONENT_LABOUR}
              />
              <LabourTable
                title="Spare part fitting"
                rows={SPARE_PART_LABOUR}
              />
            </div>
          </section>

          {/* Promises ---------------------------------------------- */}
          <section className="service-promises">
            {SERVICE_PROMISES.map((p) => (
              <div className="promise" key={p.title}>
                <strong>{p.title}</strong>
                <span>{p.subtitle}</span>
              </div>
            ))}
          </section>

          {/* Terms -------------------------------------------------- */}
          <section className="service-terms">
            <h2>Good to know</h2>
            <ul>
              <li>
                We quote before we start. Any work beyond the package is shown to
                you as a written estimate and only carried out once you approve
                it.
              </li>
              <li>
                Workmanship on a chargeable repair is warranted for 30 days on
                that specific repair. Parts we fit carry the part manufacturer's
                warranty where one exists.
              </li>
              <li>
                Booked service appointments can be cancelled free of charge up to
                24 hours before. Once a part has been ordered specifically for
                your cycle, that part is payable — see our{" "}
                <Link to="/refund-policy">Refund &amp; Cancellation Policy</Link>.
              </li>
              <li>
                Diagnostic charges, where they apply, are disclosed in advance and
                payable once the diagnosis is complete, whether or not you proceed
                with the repair.
              </li>
              <li>
                Servicing is carried out at our Khagaul showroom during{" "}
                {BUSINESS.supportHours}.
              </li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
